import {
  type BudgetRuleInput,
  type BudgetRuleState,
  budgetRuleSchema,
  campaignBudgetRulesSchema,
  recommendedBudgetRuleEventSchema,
} from "@adpilot/shared";
import { z } from "zod";
import { api } from "./api";
import { CAMPAIGN_STATE_TIMEOUT_MS } from "./campaign-api";

/** ADP-222: Amazon SP budget rules attached to a campaign. */
export async function fetchCampaignBudgetRules(campaignId: string) {
  const res = await api
    .get(`campaigns/${campaignId}/budget-rules`)
    .json<{ success: true; data: unknown }>();
  return campaignBudgetRulesSchema.parse(res.data);
}

export async function fetchRecommendedRuleEvents(campaignId: string) {
  const res = await api
    .get(`campaigns/${campaignId}/budget-rules/recommended-events`, {
      timeout: CAMPAIGN_STATE_TIMEOUT_MS,
    })
    .json<{ success: true; data: unknown }>();
  return z.array(recommendedBudgetRuleEventSchema).parse(res.data);
}

// Amazon writes can outlive ky's 10s default (the server bounds each leg).
export async function createBudgetRule(campaignId: string, input: BudgetRuleInput) {
  const res = await api
    .post(`campaigns/${campaignId}/budget-rules`, {
      json: input,
      timeout: CAMPAIGN_STATE_TIMEOUT_MS,
    })
    .json<{ success: true; data: unknown }>();
  return budgetRuleSchema.parse(res.data);
}

export async function updateBudgetRule(campaignId: string, ruleId: string, input: BudgetRuleInput) {
  const res = await api
    .put(`campaigns/${campaignId}/budget-rules/${ruleId}`, {
      json: input,
      timeout: CAMPAIGN_STATE_TIMEOUT_MS,
    })
    .json<{ success: true; data: unknown }>();
  return budgetRuleSchema.parse(res.data);
}

export async function setBudgetRuleState(
  campaignId: string,
  ruleId: string,
  state: BudgetRuleState,
) {
  const res = await api
    .patch(`campaigns/${campaignId}/budget-rules/${ruleId}/state`, {
      json: { state },
      timeout: CAMPAIGN_STATE_TIMEOUT_MS,
    })
    .json<{ success: true; data: unknown }>();
  return budgetRuleSchema.parse(res.data);
}

export async function retryAttachBudgetRule(campaignId: string, ruleId: string) {
  const res = await api
    .post(`campaigns/${campaignId}/budget-rules/${ruleId}/attach`, {
      timeout: CAMPAIGN_STATE_TIMEOUT_MS,
    })
    .json<{ success: true; data: unknown }>();
  return budgetRuleSchema.parse(res.data);
}

export async function detachBudgetRule(campaignId: string, ruleId: string) {
  await api.delete(`campaigns/${campaignId}/budget-rules/${ruleId}`, {
    timeout: CAMPAIGN_STATE_TIMEOUT_MS,
  });
}
